import { AlertTriangle, CloudUpload, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/dialog';
import { useContainer, useSessionState } from '@/features/session/use-session';
import type { SyncDoCartao } from './fila-da-conferencia';

/**
 * O que um lugar da tela tem na fila do aparelho (Fase 43): "aguardando envio", ou o recusado com o
 * motivo do servidor, "Tentar de novo" e "Descartar" (RN-604, RN-608).
 *
 * `pendente` é o texto do selo ("Aguardando envio", "1 alteração do RDO aguardando envio");
 * `recusado`, o começo da frase do recusado ("O servidor não aceitou a medição feita sem sinal").
 * As duas ações são da fila, e moram aqui: tentar de novo põe o registro de volta e sobe a fila;
 * descartar pergunta antes, porque o que ele faria não acontece mais.
 */
export function AvisoDaFila({
  sync,
  pendente,
  recusado,
}: {
  sync: SyncDoCartao;
  pendente: string;
  recusado: string;
}) {
  const container = useContainer();
  const { session } = useSessionState();
  const [descartando, setDescartando] = useState(false);

  if (sync.state === 'pending') {
    return (
      <Badge tone="info" className="self-start" icon={<CloudUpload aria-hidden="true" />}>
        {pendente}
      </Badge>
    );
  }

  const recusadoId = sync.rejectedId;
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 rounded-md border border-(--color-state-danger) px-3 py-2 text-sm"
    >
      <p className="flex items-start gap-2 text-(--color-state-danger)">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <span>
          {recusado}
          {sync.message === undefined ? '.' : `: ${sync.message}`}
        </span>
      </p>
      <p className="text-xs text-(--color-content-muted)">
        A tela mostra o que foi feito no aparelho. Tente de novo depois de corrigir, ou descarte e
        faça outra vez.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          className="min-h-11 md:h-9"
          onClick={() => {
            if (recusadoId !== undefined && session !== undefined) {
              void container.outbox.retry(recusadoId, session);
            }
          }}
        >
          <RotateCcw aria-hidden="true" />
          Tentar de novo
        </Button>
        <Button variant="ghost" className="min-h-11 md:h-9" onClick={() => setDescartando(true)}>
          <Trash2 aria-hidden="true" />
          Descartar
        </Button>
      </div>
      <ConfirmDialog
        open={descartando}
        onOpenChange={setDescartando}
        title="Descartar o que foi feito sem sinal?"
        description="O registro sai da fila do aparelho e não vai mais para o servidor. A tela volta a mostrar o que o servidor tem. Para registrar de novo, faça outra vez."
        confirmLabel="Descartar"
        destructive
        onConfirm={() => {
          setDescartando(false);
          if (recusadoId !== undefined) void container.outbox.discard(recusadoId);
        }}
      />
    </div>
  );
}
